import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import ErrorBanner from '../components/ErrorBanner';
import type { ProjectInvitationPreview } from '../types';
import { reportErrorLocal } from '../utils/errors';
import './InvitationAcceptanceLane40.css';
import {
  clearPendingInvitationToken,
  invitationTokenFromLocation,
  takePendingInvitationToken
} from '../utils/invitationNavigation';

type Props = { apiClient: ApiClient; setError: (message: string | null) => void };

export default function InvitationAcceptancePage({ apiClient, setError }: Props) {
  const params = useParams();
  // New links use the fragment so the token is never sent in the initial page
  // request. App captures it before an OIDC redirect, and this page consumes
  // the short-lived session value immediately after the redirect returns.
  const [token] = useState(() => {
    const directToken = invitationTokenFromLocation(window.location.pathname, window.location.hash);
    if (directToken) {
      clearPendingInvitationToken(window.sessionStorage);
      return directToken;
    }
    return takePendingInvitationToken(window.sessionStorage) || params.token || '';
  });
  const [preview, setPreview] = useState<ProjectInvitationPreview | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.post<ProjectInvitationPreview>('/api/project-invitations/preview', { token });
      setPreview(response);
    } catch (err) {
      reportErrorLocal('Failed to load project invitation', err, setError, setPageError);
    } finally {
      setLoading(false);
    }
  }, [apiClient, setError, token]);

  useEffect(() => { void loadPreview(); }, [loadPreview]);

  async function accept() {
    setAccepting(true);
    setPageError(null);
    try {
      const result = await apiClient.post<{ app_id: string }>('/api/project-invitations/accept', { token });
      clearPendingInvitationToken(window.sessionStorage);
      // Reload so `/api/me` reflects the newly active membership before the
      // application decides whether to show the project console or waitlist.
      window.location.assign(`/apps/${result.app_id}`);
    } catch (err) {
      reportErrorLocal('Failed to accept project invitation', err, setError, setPageError);
    } finally {
      setAccepting(false);
    }
  }

  return <InvitationAcceptanceLane40View
    preview={preview}
    loading={loading}
    accepting={accepting}
    pageError={pageError}
    onAccept={() => void accept()}
    onDismissError={() => setPageError(null)}
    onCancel={() => clearPendingInvitationToken(window.sessionStorage)}
  />;
}

// Presentation receives no invitation token or API client.
export function InvitationAcceptanceLane40View({ preview, loading, accepting, pageError, onAccept, onDismissError, onCancel }: {
  preview: ProjectInvitationPreview | null;
  loading: boolean;
  accepting: boolean;
  pageError: string | null;
  onAccept: () => void;
  onDismissError: () => void;
  onCancel: () => void;
}) {
  const invitation = preview?.invitation;
  return (
    <section className="invitation-lane40" aria-labelledby="invitation-lane40-title">
      <header className="invitation-lane40__header">
        <p className="invitation-lane40__eyebrow">Project access</p>
        <h2 id="invitation-lane40-title">Project invitation</h2>
        <p>Review your invitation before joining the project.</p>
      </header>
      <div className="invitation-lane40__body">
        {!preview ? (
          loading ? (
            <div className="invitation-lane40__state" role="status">
              <h3>Loading invitation…</h3>
              <p>Checking the invitation for your signed-in account.</p>
            </div>
          ) : (
            <div className="invitation-lane40__state invitation-lane40__state--warning" role="alert">
              <h3>Invitation could not be loaded</h3>
              <p>The link may be inactive or intended for another account, or the service may be unavailable.</p>
              <p>Check that you are signed in with the invited email address. If needed, ask the project owner for a new invitation.</p>
            </div>
          )
        ) : invitation && (
          <>
            {pageError && <div role="alert"><ErrorBanner message={pageError} onDismiss={onDismissError} /></div>}
            <div className="invitation-lane40__project">
              <p className="invitation-lane40__label">Invited project</p>
              <h3>{invitation.app_name}</h3>
            </div>
            <dl className="invitation-lane40__details">
              <div><dt>Invited email</dt><dd>{invitation.email}</dd></div>
              <div><dt>Project role</dt><dd>{invitation.role}</dd></div>
              <div><dt>Expires</dt><dd>{invitation.expires_at ? new Date(invitation.expires_at).toLocaleString() : '—'}</dd></div>
            </dl>
            <aside className="invitation-lane40__notice" aria-labelledby="invitation-lane40-access">
              <h3 id="invitation-lane40-access">What accepting means</h3>
              <p>Accepting adds your account to this project. It does not grant deploy, release, secret, or member-management access unless the server explicitly grants it.</p>
            </aside>
            {!preview.can_accept && <div className="invitation-lane40__state invitation-lane40__state--warning" role="status">
              <h3>Invitation unavailable</h3>
              <p>This invitation cannot be accepted. It may be expired, revoked, or intended for another account.</p>
            </div>}
          </>
        )}
        <div className="invitation-lane40__actions">
          {preview && <button type="button" disabled={!preview.can_accept || accepting} onClick={onAccept}>{accepting ? 'Accepting…' : 'Accept invitation'}</button>}
          <Link className="link-button secondary" to="/apps" onClick={onCancel}>
            Cancel
          </Link>
        </div>
        {accepting && <p className="invitation-lane40__progress" role="status">Accepting invitation. The project will open when this completes.</p>}
      </div>
    </section>
  );
}
